import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    ArrowUpRight,
    Building2,
    CreditCard,
    IndianRupee,
    ShieldCheck,
    Smartphone,
    Wallet,
    Zap,
} from "lucide-react";
import { useWallet } from "@/context/WalletContext";

const PAYMENT_METHODS = [
    {
        id: "razorpay",
        name: "Razorpay",
        description: "Pay securely using UPI, cards, net banking, or supported wallets.",
        image: "/resources/images/razorpay.png",
        href: "/razorpay",
        icon: CreditCard,
        badge: "Recommended",
        accent: "from-[#1d6fe8] to-[#635bff]",
    },
    {
        id: "payumoney",
        name: "PayU Money",
        description: "Add money through cards, UPI, net banking, and other payment modes.",
        image: "/resources/images/payu.png",
        href: "/payumoney",
        icon: Building2,
        badge: "Multiple Options",
        accent: "from-[#00a884] to-[#21c063]",
    },
    {
        id: "upi",
        name: "Manual Payment",
        description: "Pay by UPI QR or bank transfer and submit your transaction details.",
        image: "/resources/images/upi.png",
        href: "/manual",
        icon: Smartphone,
        badge: "UPI / Bank",
        accent: "from-[#ff7a18] to-[#ff3d81]",
    },
];

export default function WalletTopup() {
    const navigate = useNavigate();
    const { walletBalance } = useWallet();
    const [availableMethods, setAvailableMethods] = useState([]);
    const [loadingMethods, setLoadingMethods] = useState(true);

    useEffect(() => {
        const loadPaymentMethods = async () => {
            try {
                const response = await fetch("/backend/api/payment_methods.php", {
                    method: "GET",
                    credentials: "include",
                    cache: "no-store",
                    headers: {
                        Accept: "application/json",
                    },
                });

                if (response.status === 401) {
                    navigate("/login", { replace: true });
                    return;
                }

                const result = await response.json();

                if (!response.ok || !result.success) {
                    throw new Error(
                        result.message || "Unable to load payment methods."
                    );
                }

                const methods = (result.methods || [])
                    .map((dbMethod) => {
                        const pgName = String(dbMethod.pgname || "")
                            .trim()
                            .toLowerCase();

                        const uiMethod = PAYMENT_METHODS.find((method) => {
                            const methodName = method.name.trim().toLowerCase();

                            // Match either the displayed name or common DB names.
                            if (methodName === pgName) return true;

                            if (
                                method.id === "payumoney" &&
                                ["payu", "payu money", "payumoney"].includes(pgName)
                            ) {
                                return true;
                            }

                            if (
                                method.id === "upi" &&
                                ["upi", "upi / deposit", "manual", "manual payment"].includes(pgName)
                            ) {
                                return true;
                            }

                            return method.id === pgName;
                        });

                        if (!uiMethod) return null;

                        return {
                            ...uiMethod,
                            href: dbMethod.route || uiMethod.href,
                            priority: Number(dbMethod.priority) || 999,
                        };
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.priority - b.priority);

                setAvailableMethods(methods);
            } catch (error) {
                console.error("Payment methods loading error:", error);
                setAvailableMethods([]);
            } finally {
                setLoadingMethods(false);
            }
        };

        loadPaymentMethods();
    }, [navigate]);

    return (
        <main className="relative min-h-[calc(100vh-65px)] overflow-hidden px-4 py-8 sm:px-6 sm:py-12">
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-1/2 top-16 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-gradient-to-tr from-[#3BC7FF]/20 to-[#7B61FF]/20 blur-3xl" />
            </div>

            <div className="relative mx-auto w-full max-w-5xl fade-in-up">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.05] hover:text-foreground"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                </button>

                <section className="glass-card overflow-hidden rounded-3xl border border-border/70">
                    <div className="relative overflow-hidden border-b border-border/70 p-6 sm:p-8">
                        <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-gradient-to-br from-emerald-500/15 to-cyan-500/10 blur-2xl" />

                        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-start gap-4">
                                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl btn-gradient text-white shadow-lg">
                                    <Wallet className="h-7 w-7" strokeWidth={2.1} />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                                        Smart Solutions Wallet
                                    </p>
                                    <h1 className="mt-1 text-2xl font-display font-bold tracking-tight sm:text-3xl">
                                        Top up your <span className="text-gradient-brand">wallet</span>
                                    </h1>
                                    <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                                        Choose your preferred secure payment method to add money to your account.
                                    </p>
                                </div>
                            </div>

                            <div className="min-w-[190px] rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-5 py-4">
                                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-600">
                                    <IndianRupee className="h-3.5 w-3.5" />
                                    Available Balance
                                </div>
                                <p className="mt-1 text-2xl font-display font-bold text-emerald-600">
                                    ₹ {formatBalance(walletBalance)}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 sm:p-8">
                        <div className="mb-5 flex items-center justify-between gap-4">
                            <div>
                                <h2 className="text-lg font-display font-bold">Select payment method</h2>
                                <p className="mt-1 text-xs text-muted-foreground">You will continue to the selected payment page.</p>
                            </div>
                            <div className="hidden items-center gap-1.5 text-xs font-medium text-emerald-600 sm:flex">
                                <ShieldCheck className="h-4 w-4" />
                                Secure payments
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-3">
                            {loadingMethods ? (
                                <div className="col-span-full py-10 text-center text-sm text-muted-foreground">
                                    Loading payment methods...
                                </div>
                            ) : availableMethods.length > 0 ? (
                                availableMethods.map((method) => (
                                    <PaymentCard key={method.id} method={method} />
                                ))
                            ) : (
                                <div className="col-span-full rounded-2xl border border-border/70 bg-background/35 px-4 py-10 text-center text-sm text-muted-foreground">
                                    No payment methods are currently available.
                                </div>
                            )}
                        </div>

                        <div className="mt-6 grid gap-3 rounded-2xl border border-border/70 bg-background/35 p-4 sm:grid-cols-3">
                            <TrustItem icon={ShieldCheck} title="Secure checkout" text="Payment information is handled by the selected gateway." />
                            <TrustItem icon={Zap} title="Fast wallet credit" text="Successful online payments can be credited quickly." />
                            <TrustItem icon={Wallet} title="Check your balance" text="Your updated balance appears in the portal header." />
                        </div>
                    </div>
                </section>

                <p className="mt-4 text-center text-[11px] uppercase tracking-[0.26em] text-muted-foreground">
                    Secured by Smart Solutions
                </p>
            </div>
        </main>
    );
}

function PaymentCard({ method }) {
    const [imageFailed, setImageFailed] = useState(false);
    const navigate = useNavigate();
    const Icon = method.icon;

    const handlePaymentMethod = () => {
        navigate(method.href);
    };

    return (
        <button
            type="button"
            onClick={handlePaymentMethod}
            className="group relative flex min-h-[245px] w-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-background/45 p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
            <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${method.accent}`} />

            <div className="flex items-start justify-between gap-3">
                <div className="flex h-14 w-24 items-center justify-center rounded-xl border border-border/60 bg-white px-2 shadow-sm">
                    {!imageFailed ? (
                        <img
                            src={method.image}
                            alt={`${method.name} logo`}
                            className="max-h-10 max-w-full object-contain"
                            onError={() => setImageFailed(true)}
                        />
                    ) : (
                        <Icon className="h-7 w-7 text-slate-600" />
                    )}
                </div>

                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
                    {method.badge}
                </span>
            </div>

            <h3 className="mt-5 text-lg font-display font-bold">{method.name}</h3>
            <p className="mt-2 flex-1 text-sm leading-6 text-muted-foreground">{method.description}</p>

            <div className="mt-5 flex items-center justify-between text-sm font-semibold text-primary">
                Continue to pay
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 transition-all group-hover:bg-primary group-hover:text-white">
                    <ArrowUpRight className="h-4 w-4" />
                </span>
            </div>
        </button>
    );
}

function TrustItem({ icon: Icon, title, text }) {
    return (
        <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
                <Icon className="h-4 w-4" />
            </span>
            <div>
                <p className="text-xs font-semibold">{title}</p>
                <p className="mt-1 text-[11px] leading-4 text-muted-foreground">{text}</p>
            </div>
        </div>
    );
}

function formatBalance(value) {
    const amount = Number(value);
    return Number.isFinite(amount)
        ? amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "0.00";
}
